/**
 * useCreateWithFiles Hook
 * Feature: 057-critique-quick-create
 *
 * Shared hook for handling file picker + drag-and-drop + creation flow.
 * Used by both "Create Collection" and "Create Critique" buttons.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { supportsFolderPicker } from '@/utils/fileExtraction';

/**
 * Hook options for useCreateWithFiles
 */
export interface UseCreateWithFilesOptions {
  /**
   * Async function that creates the resource (collection, session, etc.)
   * Called with optional files after user selects them.
   *
   * @param files - Optional array of File objects from file picker or drag-drop
   * @returns Promise that resolves when creation completes
   * @throws Error if creation fails
   */
  onCreate: (files?: File[]) => Promise<void>;

  /**
   * Async function that creates the resource with existing photo IDs.
   * Called when user selects photos from the Existing Photos modal.
   *
   * @param photoIds - Array of photo global IDs to add to the new collection
   * @returns Promise that resolves when creation completes
   */
  onCreateWithExistingPhotos?: (photoIds: string[]) => Promise<void>;

  /**
   * Whether the creation flow is disabled (e.g., user not authenticated)
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether to show folder upload option
   * @default true
   */
  showFolderOption?: boolean;

  /**
   * Whether to show existing photos option
   * @default true (if onCreateWithExistingPhotos is provided)
   */
  showExistingPhotosOption?: boolean;
}

/**
 * Return value from useCreateWithFiles hook
 */
export interface UseCreateWithFilesReturn {
  /**
   * Ref to hidden file input element
   * Should be rendered in component and passed to this ref
   */
  fileInputRef: React.RefObject<HTMLInputElement>;

  /**
   * Callback ref for folder input element
   * Sets webkitdirectory attribute when element mounts
   */
  setFolderInputRef: (node: HTMLInputElement | null) => void;

  /**
   * Ref to dropdown menu element (for click-outside detection)
   */
  menuRef: React.RefObject<HTMLDivElement>;

  /**
   * Handler for button click
   * Shows upload menu if folder support enabled, otherwise opens file picker
   */
  handleButtonClick: () => void;

  /**
   * Handler for "Select Files" menu option
   */
  handleSelectFiles: () => void;

  /**
   * Handler for "Select Folder" menu option
   */
  handleSelectFolder: () => void;

  /**
   * Handler for files dropped on button
   * Validates files and calls onCreate with valid files
   *
   * @param files - Array of File objects from drag-and-drop
   */
  handleFilesDropped: (files: File[]) => void;

  /**
   * Handler for invalid files notification
   * Logs invalid file summary via structured logger
   *
   * @param summary - Human-readable summary of invalid files
   */
  handleInvalidFiles: (summary: string) => void;

  /**
   * Whether creation is in progress
   * Used to show loading state on button
   */
  isPending: boolean;

  /**
   * Whether the upload menu dropdown is visible
   */
  showUploadMenu: boolean;

  /**
   * Whether folder selection is supported by the browser
   */
  folderSupported: boolean;

  /**
   * Whether to show the folder option in the menu
   */
  showFolderOption: boolean;

  /**
   * Whether to show the existing photos option in the menu
   */
  showExistingPhotosOption: boolean;

  /**
   * Whether the existing photos modal is visible
   */
  showExistingPhotosModal: boolean;

  /**
   * Handler to show the existing photos modal
   */
  handleSelectExistingPhotos: () => void;

  /**
   * Handler when photos are selected from the existing photos modal
   */
  handleExistingPhotosSelected: (photoIds: string[]) => void;

  /**
   * Handler to close the existing photos modal
   */
  handleCloseExistingPhotosModal: () => void;

  /**
   * Error message from failed operations (null if no error)
   */
  error: string | null;

  /**
   * Clears the current error state
   */
  clearError: () => void;
}

/**
 * Shared hook for file picker + drag-and-drop + creation pattern
 *
 * Manages file input state, handles button clicks to open file picker,
 * processes drag-dropped files, and coordinates with creation handler.
 *
 * @param options - Hook configuration
 * @returns Hook handlers and state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const navigate = useNavigate();
 *   const createCollection = useCreateCollection();
 *
 *   const handleCreate = async (files?: File[]) => {
 *     const collection = await createCollection.mutateAsync({ name: 'My Collection' });
 *     navigate(`/collections/${collection.globalId}`, { state: { initialFiles: files } });
 *   };
 *
 *   const {
 *     fileInputRef,
 *     handleButtonClick,
 *     handleFilesDropped,
 *     handleInvalidFiles,
 *     isPending
 *   } = useCreateWithFiles({
 *     onCreate: handleCreate,
 *     disabled: !user
 *   });
 *
 *   return (
 *     <>
 *       <input
 *         ref={fileInputRef}
 *         type="file"
 *         accept="image/jpeg,image/png,image/tiff"
 *         onChange={(e) => {
 *           const files = Array.from(e.target.files || []);
 *           if (files.length > 0) handleCreate(files);
 *         }}
 *         multiple
 *         className="hidden"
 *       />
 *       <FileDropZone
 *         onFilesDropped={handleFilesDropped}
 *         onInvalidFiles={handleInvalidFiles}
 *         disabled={isPending || !user}
 *       >
 *         <Button
 *           onClick={handleButtonClick}
 *           loading={isPending}
 *           disabled={isPending || !user}
 *         >
 *           Create
 *         </Button>
 *       </FileDropZone>
 *     </>
 *   );
 * }
 * ```
 */
export function useCreateWithFiles(
  options: UseCreateWithFilesOptions
): UseCreateWithFilesReturn {
  const {
    onCreate,
    onCreateWithExistingPhotos,
    disabled = false,
    showFolderOption = true,
    showExistingPhotosOption = !!onCreateWithExistingPhotos
  } = options;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isPending, setIsPending] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showExistingPhotosModal, setShowExistingPhotosModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if folder picker is supported by the browser
  const folderSupported = supportsFolderPicker();

  // Callback ref to set webkitdirectory attribute when element mounts
  const setFolderInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');
    }
    (folderInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showUploadMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUploadMenu(false);
      }
    };

    // Delay adding listener to prevent immediate close from the click that opened the menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUploadMenu]);

  /**
   * Opens file picker or shows menu when button is clicked
   */
  const handleButtonClick = () => {
    if (disabled || isPending) {
      return;
    }

    // If folder support is enabled and browser supports it, show the menu
    if (showFolderOption && folderSupported) {
      setShowUploadMenu(!showUploadMenu);
    } else {
      // Otherwise, directly open file picker
      fileInputRef.current?.click();
    }
  };

  /**
   * Handler for "Select Files" menu option
   */
  const handleSelectFiles = () => {
    setShowUploadMenu(false);
    fileInputRef.current?.click();
  };

  /**
   * Handler for "Select Folder" menu option
   */
  const handleSelectFolder = () => {
    setShowUploadMenu(false);
    folderInputRef.current?.click();
  };

  /**
   * Handler for "Existing Photos" menu option
   */
  const handleSelectExistingPhotos = () => {
    setShowUploadMenu(false);
    setShowExistingPhotosModal(true);
  };

  /**
   * Handler when photos are selected from the existing photos modal
   * Keeps modal open during async operation, only closes on success
   */
  const handleExistingPhotosSelected = async (photoIds: string[]) => {
    if (disabled || isPending || photoIds.length === 0 || !onCreateWithExistingPhotos) {
      return;
    }

    setError(null);
    setIsPending(true);
    try {
      await onCreateWithExistingPhotos(photoIds);
      // Only close modal on success
      setShowExistingPhotosModal(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create with selected photos';
      setError(message);
      logger.error('useCreateWithFiles', 'Creation failed with existing photos:', err);
    } finally {
      setIsPending(false);
    }
  };

  /**
   * Handler to close the existing photos modal
   */
  const handleCloseExistingPhotosModal = () => {
    setShowExistingPhotosModal(false);
    setError(null); // Clear any error when closing
  };

  /**
   * Clear error state manually
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Handles files dropped onto the button
   * Calls onCreate with the dropped files
   */
  const handleFilesDropped = async (files: File[]) => {
    if (disabled || isPending || files.length === 0) {
      return;
    }

    setError(null);
    setIsPending(true);
    try {
      await onCreate(files);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create with uploaded files';
      setError(message);
      logger.error('useCreateWithFiles', 'Creation failed during drag-drop:', err);
    } finally {
      setIsPending(false);
    }
  };

  /**
   * Handles invalid files notification from FileDropZone
   * Logs warning with summary of invalid files
   */
  const handleInvalidFiles = (summary: string) => {
    logger.warn('useCreateWithFiles', 'Invalid files filtered:', summary);
  };

  return {
    fileInputRef,
    setFolderInputRef,
    menuRef,
    handleButtonClick,
    handleSelectFiles,
    handleSelectFolder,
    handleFilesDropped,
    handleInvalidFiles,
    isPending,
    showUploadMenu,
    folderSupported,
    showFolderOption,
    showExistingPhotosOption,
    showExistingPhotosModal,
    handleSelectExistingPhotos,
    handleExistingPhotosSelected,
    handleCloseExistingPhotosModal,
    error,
    clearError
  };
}
